import { PhantomTypeArgument, StructClass } from '@/shared/gen/_framework/reified'
import { ChartEntry } from './types'
import { ClmmPool, Price } from '@kunalabs-io/kai'
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { useCurrentClient } from '@mysten/dapp-kit-react'
import { ClientWithCoreApi } from '@mysten/sui/client'
import { fetchTicks as cetusFetchTicks } from '@/shared/cetus'
import Decimal from 'decimal.js'

export interface TickProcessed {
  tick: number
  liquidityActive: bigint
  liquidityNet: bigint
  price0: string
  // sdkPrice: Price<Token, Token>
}

interface DensityChartEntry {
  activeLiquidity: number
  price0: number
}

export const poolTicksQueryKey = (poolId?: string) => ['pool-ticks', poolId] as const

export function useDensityChartData(
  pool: ClmmPool<StructClass, unknown, PhantomTypeArgument, PhantomTypeArgument>
): UseQueryResult<ChartEntry[]> {
  const client = useCurrentClient()

  return useQuery({
    queryKey: poolTicksQueryKey(pool?.id),
    enabled: !!pool,
    queryFn: async () => {
      if (!pool) {
        throw new Error('Invariant violation: pool is required')
      }

      return await getDensityChartData(client, pool)
    },
  })
}

export async function getDensityChartData(
  client: ClientWithCoreApi,
  pool: ClmmPool<StructClass, unknown, PhantomTypeArgument, PhantomTypeArgument>
): Promise<DensityChartEntry[]> {
  if (pool.isCetus()) {
    const ticks = await cetusFetchTicks({
      client,
      poolId: pool.id,
      coinTypeA: pool.X.typeName,
      coinTypeB: pool.Y.typeName,
    })

    let currentL = 0n
    const n = ticks.length
    const chartData: DensityChartEntry[] = []
    for (let i = 0; i < n - 1; i++) {
      const tick = ticks[i]
      currentL = currentL + tick.liquidityNet

      const sqrtPriceX64 = new Decimal(tick.sqrtPrice.toString())
      const numeric = sqrtPriceX64.pow(2).div((1n << 128n).toString())
      const price = Price.fromNumeric(pool.X, pool.Y, numeric)

      chartData.push({
        activeLiquidity: Number(currentL),
        price0: price.human.toNumber(),
      })
    }

    return chartData
  } else if (pool.isBluefin()) {
    let apiUrl: string = `https://swap.api.sui-prod.bluefin.io`
    if (import.meta.env.VITE_BLUEFIN_API_URL_OVERRIDE) {
      apiUrl = import.meta.env.VITE_BLUEFIN_API_URL_OVERRIDE
    }

    const response = await fetch(`${apiUrl}/api/v1/pool/line/ticks?pool=${pool.id}`)
    const data = (await response.json()) as { liquidity: string; price: string; tick: number }[]
    return data.map(item => ({
      activeLiquidity: Number(item.liquidity),
      price0: Number(item.price),
    }))
  } else {
    throw new Error('TODO: support other pool types')
  }
}
